/**
 * src/lib/photos/cropToRegion.js — "zoom into the annotated box" geometry.
 *
 * TICKET-070E. A mark is usually a SMALL crop of a big photo (a defect, a plug,
 * a seam). Both the lightbox in-place zoom (E) and the report's per-mark crops
 * (F) need the same thing: the rectangular WINDOW of the image to show for a
 * given mark — its bounding box, padded for context, never smaller than a
 * usable minimum, and clamped to stay inside the image.
 *
 * All inputs/outputs are NORMALIZED 0..1 (a fraction of the image), exactly like
 * src/lib/photos/regionShapes.js — so the result is resolution-independent and a
 * CSS crop (RegionCrop) can render it without knowing the original pixel size.
 *
 * Pure + DOM-free (single unit test: tests/lib/photos/cropToRegion.test.js).
 */
import { regionBBox, isValidRegion } from './regionShapes';

// A little context around the mark, and a floor so a pin/tiny box still shows
// enough of the surrounding photo to make sense. Fractions of the image.
export const DEFAULT_CROP_PAD = 0.12;
export const DEFAULT_CROP_MIN = 0.2;

function clamp01(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Crop window (normalized {x,y,w,h}) for a mark's region.
 * @param {object} region — a normalized region (rect/ellipse/arrow/pin)
 * @param {{pad?:number, min?:number}} [opts]
 *   pad — context added on EACH side (fraction of the image). Default 0.12.
 *   min — minimum crop width/height (fraction of the image). Default 0.2.
 * @returns {{x:number,y:number,w:number,h:number}|null} null for an invalid region
 */
export function cropToRegion(region, opts = {}) {
  if (!isValidRegion(region)) return null;
  const b = regionBBox(region);
  if (!b) return null;
  const pad = typeof opts.pad === 'number' ? opts.pad : DEFAULT_CROP_PAD;
  const min = typeof opts.min === 'number' ? opts.min : DEFAULT_CROP_MIN;

  // 1. pad on each side around the centre.
  let x = b.x - pad;
  let y = b.y - pad;
  let w = b.w + 2 * pad;
  let h = b.h + 2 * pad;

  // 2. enforce the minimum window, growing symmetrically about the centre.
  if (w < min) { x -= (min - w) / 2; w = min; }
  if (h < min) { y -= (min - h) / 2; h = min; }

  // 3. clamp to the image: cap size at full, then slide fully in-bounds.
  if (w > 1) { w = 1; }
  if (h > 1) { h = 1; }
  x = Math.min(Math.max(x, 0), 1 - w);
  y = Math.min(Math.max(y, 0), 1 - h);

  return { x, y, w, h };
}
